import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from 'UIPackage/MainModule/components/Button';

import styles from './SelectTable.module.scss';

interface ISelectTableProps<T> {
  items: T[];
  theme?: string;
  onItemSet: (items: T[]) => void;
}

interface ISelectTableItem {
  id: string;
  isSelected?: boolean;
  name: string;
}

export function SelectTable<T extends ISelectTableItem>({ theme, items, onItemSet }: ISelectTableProps<T>) {
  const [searchInputValue, setSearchInputValue] = useState('');
  const [isSelectionOffButtonEnabled, setIsSelectionOffButtonEnabled] = useState(false);
  const [itemsSortOrder, setItemsSortOrder] = useState<'ascend' | 'descend'>('ascend');
  const filteredItems = useMemo(
    () =>
      items
        .filter(item => item.name.toLowerCase().includes(searchInputValue.toLowerCase()))
        .sort((a: T, b: T) => {
          if (a.name < b.name) {
            return itemsSortOrder === 'ascend' ? -1 : 1;
          }
          if (a.name > b.name) {
            return itemsSortOrder === 'ascend' ? 1 : -1;
          }

          return 0;
        }),
    [items, itemsSortOrder, searchInputValue],
  );

  useEffect(() => {
    const isAnyItemSelected = items.some(item => item.isSelected);

    setIsSelectionOffButtonEnabled(isAnyItemSelected);
  }, [items]);

  const handleSearchInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setSearchInputValue(e.target.value),
    [],
  );

  const handleSelectionOnButtonClick = useCallback(() => {
    const newItems = [...items];

    onItemSet(
      newItems.map(item => {
        return {
          ...item,
          isSelected: true,
        };
      }),
    );
  }, [items, onItemSet]);

  const handleSelectionOffButtonClick = () => {
    const newItems = [...items];

    onItemSet(
      newItems.map(item => {
        return {
          ...item,
          isSelected: false,
        };
      }),
    );
  };

  const handleFieldClick = (id: string | number | symbol) => () => {
    const newItems = [...items];

    onItemSet(
      newItems.map((item: T) => {
        if (item.id === id) {
          return {
            ...item,
            isSelected: typeof item.isSelected === 'boolean' ? !item.isSelected : false,
          };
        }

        return item;
      }),
    );
  };

  const handleFieldsHeadCellClick = useCallback(() => {
    const newSortOrderValue = itemsSortOrder === 'ascend' ? 'descend' : 'ascend';

    setItemsSortOrder(newSortOrderValue);
  }, [itemsSortOrder]);

  const tableBlockAttributes = {
    ...(theme === 'tableColumns' && { 'data-columns': true }),
  };

  const headCellIconAttributes = {
    ...(itemsSortOrder !== 'ascend' && { 'data-alt': true }),
  };

  return (
    <div>
      <div className={styles.TableManage}>
        <div className={styles.Search}>
          <label className={styles.SearchLabel} htmlFor="select-table-search-value">
            Поиск:
          </label>
          <input
            className={styles.SearchInput}
            id="select-table-search-value"
            value={searchInputValue}
            type="text"
            onChange={handleSearchInputChange}
          />
        </div>
        <div className={styles.SelectionButtons}>
          <Button
            className={styles.SelectionOnButton}
            text="Выделить все"
            size="medium"
            onClick={handleSelectionOnButtonClick}
          />
          <Button
            className={styles.SelectionOffButton}
            text="Снять выделение"
            size="medium"
            onClick={handleSelectionOffButtonClick}
            disabled={!isSelectionOffButtonEnabled}
          />
        </div>
      </div>
      <div className={styles.TableBlock} {...tableBlockAttributes}>
        <div className={styles.Table}>
          <div className={styles.Head} onClick={handleFieldsHeadCellClick}>
            Название поля
            <span className={styles.HeadCellIcon} {...headCellIconAttributes} />
          </div>
          {filteredItems.length ? (
            filteredItems.map(item => {
              const fieldAttributes = {
                ...((item.isSelected ?? true) && { 'data-selected': true }),
                ...(theme === 'tableColumns' && { 'data-column': true }),
              };

              return (
                <div
                  key={item.id.toString()}
                  className={styles.Field}
                  onClick={handleFieldClick(item.id)}
                  {...fieldAttributes}
                >
                  {item.name}
                </div>
              );
            })
          ) : (
            <div className={styles.FieldIsEmpty}>Такого поля нет</div>
          )}
        </div>
      </div>
    </div>
  );
}
